import { useMemo, useState } from 'react';
import { api } from '@/api/client';
import { AppIcon } from '@/components/AppIcon';
import { ActionBar, DataGrid, StatusBar, type GridColumn } from '@/components/desktop';
import { ReverseButton } from '@/components/documents';
import { SearchSelect } from '@/components/SearchSelect';
import {
  ErrorNote,
  Field,
  FormActions,
  FormGroup,
  FormPanel,
  OkNote,
  Pager,
  ScreenWithForm,
  Select,
  StatusPill,
  TextArea,
  errorMessage,
  todayIso,
} from '@/components/ui';
import { formatDate, formatMoney, formatQuantity, roundMoney } from '@/format';
import {
  SALE_KIND_LABELS,
  useBuyers,
  useCashBoxes,
  useOtherSales,
  useSalesMutation,
  type OtherSaleRow,
} from '@/hooks/sales';
import { useAuth } from '@/stores/auth';
import { useFarmScope } from '@/stores/farmScope';

const PAGE = 50;

const KINDS = Object.entries(SALE_KIND_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const STATUSES = [
  { value: '', label: 'All' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'POSTED', label: 'Posted' },
  { value: 'REVERSED', label: 'Reversed' },
];

/** What each kind is counted in, so the quantity box can say so. */
const UNITS: Record<string, string> = {
  EMPTY_BAG: 'bags',
  MANURE: 'trolleys',
  SCRAP_OTHER: 'kg',
};

interface Draft {
  farmId: string;
  buyerId: string;
  kind: string;
  soldOn: string;
  qty: string;
  rate: string;
  paidInCash: boolean;
  intoCashBoxId: string;
  remarks: string;
}

function emptyDraft(farmId: string): Draft {
  return {
    farmId,
    buyerId: '',
    kind: 'EMPTY_BAG',
    soldOn: todayIso(),
    qty: '',
    rate: '',
    paidInCash: true,
    intoCashBoxId: '',
    remarks: '',
  };
}

/**
 * Sales & Dispatch › Other Sales.
 *
 * Empty bags, manure and scrap on one screen with a kind, because it is the same
 * paperwork with a different load on it — and the chart of accounts already tells
 * the three apart, so they need no screen each.
 *
 * A rate of nil is allowed and says so: manure is sometimes given away to get it
 * off the farm, and recording that as a sale of nothing is more honest than not
 * recording who took it.
 */
export function OtherSales() {
  const can = useAuth((s) => s.can);
  const farmIds = useFarmScope((s) => s.selectedFarmIds);

  const [kind, setKind] = useState('');
  const [status, setStatus] = useState('');
  const [skip, setSkip] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<OtherSaleRow | null>(null);
  const [draft, setDraft] = useState<Draft>(() => emptyDraft(farmIds[0] ?? ''));
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const sales = useOtherSales({ farmIds, kind, status, take: PAGE, skip });
  const buyers = useBuyers();
  const cashBoxes = useCashBoxes(draft.farmId ? [draft.farmId] : farmIds);
  const mutate = useSalesMutation<unknown>();

  const rows = sales.data?.items ?? [];
  const total = sales.data?.total ?? 0;
  const current = rows.find((r) => r.id === selected) ?? null;

  const amount = useMemo(() => {
    const q = Number(draft.qty);
    const r = Number(draft.rate);
    if (!Number.isFinite(q) || !Number.isFinite(r) || draft.qty === '' || draft.rate === '') {
      return null;
    }
    return roundMoney(q * r).toFixed(2);
  }, [draft.qty, draft.rate]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  function startAdd() {
    setEditing(null);
    setDraft(emptyDraft(farmIds[0] ?? ''));
    setError('');
    setNote('');
    setShowForm(true);
  }

  function startEdit(row: OtherSaleRow) {
    setEditing(row);
    setDraft({
      farmId: row.farmId,
      buyerId: row.buyerId,
      kind: row.kind,
      soldOn: row.soldOn,
      qty: row.qty,
      rate: row.rate,
      paidInCash: row.paidInCash,
      intoCashBoxId: row.intoCashBoxId ?? '',
      remarks: row.remarks ?? '',
    });
    setError('');
    setNote('');
    setShowForm(true);
  }

  function problem(): string | null {
    if (!draft.farmId) return 'Pick the farm';
    if (!draft.buyerId) return 'Pick the buyer';
    if (!draft.soldOn) return 'Enter the date';
    if (draft.soldOn > todayIso()) return 'A sale cannot be dated in the future';
    const q = Number(draft.qty);
    if (!Number.isFinite(q) || q <= 0) return 'Quantity must be more than zero';
    const r = Number(draft.rate);
    if (!Number.isFinite(r) || r < 0) return 'A rate cannot be less than zero';
    if (draft.paidInCash && !draft.intoCashBoxId) {
      return 'Say which cash box took the money, or put it on the buyer’s account';
    }
    return null;
  }

  async function save() {
    const wrong = problem();
    setError(wrong ?? '');
    setNote('');
    if (wrong) return;

    const body = {
      farmId: draft.farmId,
      buyerId: draft.buyerId,
      kind: draft.kind,
      soldOn: draft.soldOn,
      qty: draft.qty,
      rate: draft.rate,
      paidInCash: draft.paidInCash,
      ...(draft.paidInCash && draft.intoCashBoxId
        ? { intoCashBoxId: draft.intoCashBoxId }
        : {}),
      ...(draft.remarks.trim() ? { remarks: draft.remarks.trim() } : {}),
    };

    try {
      if (editing) {
        await mutate.mutateAsync(() =>
          api.patch(`/api/v1/sd/other-sales/${editing.id}`, {
            version: editing.version,
            ...body,
          }),
        );
      } else {
        await mutate.mutateAsync(() => api.post('/api/v1/sd/other-sales', body));
      }
      setNote(editing ? 'Sale saved.' : 'Sale recorded.');
      setShowForm(false);
      setEditing(null);
    } catch (e) {
      setError(errorMessage(e, 'That sale could not be saved'));
    }
  }

  async function post(row: OtherSaleRow) {
    setError('');
    try {
      await mutate.mutateAsync(() =>
        api.post(`/api/v1/sd/other-sales/${row.id}/post`, { version: row.version }),
      );
      setNote(`${row.documentNo} posted.`);
    } catch (e) {
      setError(errorMessage(e, 'That sale could not be posted'));
    }
  }

  async function remove(row: OtherSaleRow) {
    setError('');
    try {
      await mutate.mutateAsync(() =>
        api.delete(`/api/v1/sd/other-sales/${row.id}?version=${row.version}`),
      );
      setNote('Draft deleted.');
      setSelected(null);
    } catch (e) {
      setError(errorMessage(e, 'That draft could not be deleted'));
    }
  }

  const columns: Array<GridColumn<OtherSaleRow>> = [
    { key: 'soldOn', header: 'Date', width: 104, render: (r) => formatDate(r.soldOn) },
    { key: 'documentNo', header: 'Number', width: 140, render: (r) => r.documentNo },
    { key: 'farmName', header: 'Farm', width: 140, render: (r) => r.farmName },
    {
      key: 'kind',
      header: 'Sold',
      width: 140,
      render: (r) => SALE_KIND_LABELS[r.kind] ?? r.kind,
    },
    { key: 'buyerName', header: 'Buyer', width: 170, render: (r) => r.buyerName },
    {
      key: 'qty',
      header: 'Quantity',
      width: 110,
      align: 'end',
      render: (r) => `${formatQuantity(r.qty, 3)} ${r.unit}`,
    },
    {
      key: 'rate',
      header: 'Rate',
      width: 100,
      align: 'end',
      render: (r) => formatMoney(r.rate),
    },
    {
      key: 'amount',
      header: 'Amount',
      width: 120,
      align: 'end',
      render: (r) => formatMoney(r.amount),
    },
    {
      key: 'paidInCash',
      header: 'Paid',
      width: 110,
      render: (r) => (r.paidInCash ? 'Cash' : 'On account'),
    },
    {
      key: 'status',
      header: 'Status',
      width: 120,
      render: (r) => <StatusPill status={r.status} />,
    },
  ];

  const form = (
    <FormPanel
      title={editing ? `Sale ${editing.documentNo}` : 'Record a sale'}
      icon={<AppIcon kind="stock" size={18} />}
      intro="Empty bags, manure or scrap. The birds themselves go through Dispatch."
    >
      <FormGroup title="What was sold" columns={2}>
        <Select
          label="Farm"
          value={draft.farmId}
          onChange={(v) => set('farmId', v)}
          options={farmIds.map((id) => ({ value: id, label: id }))}
          required
          disabled={Boolean(editing)}
        />
        <Select
          label="Sold"
          value={draft.kind}
          onChange={(v) => set('kind', v)}
          options={KINDS}
          required
          disabled={Boolean(editing)}
        />
        <SearchSelect
          label="Buyer"
          value={draft.buyerId}
          onChange={(option) => set('buyerId', option?.value ?? '')}
          options={(buyers.data?.items ?? []).map((b) => ({
            value: b.id,
            label: b.nameEn,
          }))}
          selectedLabel={editing?.buyerName}
          required
        />
        <Field
          label="Date"
          type="date"
          value={draft.soldOn}
          onChange={(v) => set('soldOn', v)}
          required
        />
      </FormGroup>

      <FormGroup title="How much" columns={2}>
        <Field
          label={`Quantity (${UNITS[draft.kind] ?? ''})`}
          type="number"
          value={draft.qty}
          onChange={(v) => set('qty', v)}
          required
        />
        <Field
          label="Rate"
          type="number"
          value={draft.rate}
          onChange={(v) => set('rate', v)}
          required
          hint="May be nil — manure is sometimes given away to clear the shed."
        />
        <Field
          label="Amount"
          value={amount === null ? '' : amount}
          onChange={() => undefined}
          disabled
          mono
          hint="Quantity times rate."
        />
      </FormGroup>

      <FormGroup title="The money" columns={2}>
        <Select
          label="Paid"
          value={draft.paidInCash ? 'cash' : 'account'}
          onChange={(v) => {
            set('paidInCash', v === 'cash');
            if (v !== 'cash') set('intoCashBoxId', '');
          }}
          options={[
            { value: 'cash', label: 'Cash, now' },
            { value: 'account', label: 'On the buyer’s account' },
          ]}
          required
        />
        {draft.paidInCash && (
          <Select
            label="Into cash box"
            value={draft.intoCashBoxId}
            onChange={(v) => set('intoCashBoxId', v)}
            options={(cashBoxes.data?.items ?? []).map((b) => ({
              value: b.id,
              label: `${b.nameEn} — ${b.farmName}`,
            }))}
            required
          />
        )}
      </FormGroup>

      <FormGroup title="Note">
        <TextArea
          label="Note"
          value={draft.remarks}
          onChange={(v) => set('remarks', v)}
        />
      </FormGroup>

      {error && <ErrorNote message={error} />}

      <FormActions onCancel={() => setShowForm(false)}>
        <button
          type="button"
          className="btn btn--primary"
          disabled={mutate.isPending}
          onClick={() => void save()}
        >
          {editing ? 'Save changes' : 'Save sale'}
        </button>
      </FormActions>
    </FormPanel>
  );

  return (
    <ScreenWithForm
      canCreate={can('sd.emptybag.create')}
      addLabel="Record a sale"
      showForm={showForm}
      onToggle={() => (showForm ? setShowForm(false) : startAdd())}
      form={form}
    >
      <div className="toolbar toolbar--wrap">
        <Select
          label="Sold"
          value={kind}
          onChange={(v) => {
            setKind(v);
            setSkip(0);
          }}
          options={KINDS}
          placeholder="All"
        />
        <Select
          label="Status"
          value={status}
          onChange={(v) => {
            setStatus(v);
            setSkip(0);
          }}
          options={STATUSES}
          placeholder="All"
        />
      </div>

      {note && <OkNote message={note} />}
      {error && !showForm && <ErrorNote message={error} />}

      <ActionBar
        actions={[
          {
            label: 'Edit',
            icon: 'edit',
            enabled: Boolean(current && current.status === 'DRAFT' && can('sd.emptybag.update')),
            reason: 'Only a draft can be edited',
            onSelect: () => current && startEdit(current),
          },
          {
            label: 'Post',
            icon: 'post',
            primary: true,
            enabled: Boolean(
              current && current.status === 'DRAFT' && can('sd.emptybag.post'),
            ),
            reason: 'Only a draft can be posted',
            onSelect: () => current && void post(current),
          },
          {
            label: 'Delete draft',
            icon: 'remove',
            danger: true,
            enabled: Boolean(
              current && current.status === 'DRAFT' && can('sd.emptybag.delete'),
            ),
            reason: 'Only a draft can be deleted',
            onSelect: () => current && void remove(current),
          },
        ]}
      >
        {current?.status === 'POSTED' && can('sd.emptybag.reverse') && (
          <ReverseButton
            label={`Reverse ${current.documentNo}`}
            pending={mutate.isPending}
            onReverse={(reason) => {
              void mutate
                .mutateAsync(() =>
                  api.post(
                    `/api/v1/sd/other-sales/${current.id}/reverse?version=${current.version}`,
                    { reason },
                  ),
                )
                .then(() => setNote('Sale reversed.'))
                .catch((e: unknown) =>
                  setError(errorMessage(e, 'That sale could not be reversed')),
                );
            }}
          />
        )}
      </ActionBar>

      <DataGrid
        id="sd-other-sales"
        label="Other sales"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        selectedKey={selected}
        onSelect={(key) => setSelected(key)}
        onOpen={(row) => row.status === 'DRAFT' && startEdit(row)}
        empty="No sales recorded yet."
        loading={sales.isLoading}
      />

      <Pager skip={skip} take={PAGE} total={total} onChange={setSkip} />

      <StatusBar>
        <span>
          {total} sale{total === 1 ? '' : 's'}
        </span>
      </StatusBar>
    </ScreenWithForm>
  );
}
